import React, { useCallback, useRef } from "react";
import { useFormikContext } from "formik";
import { Scrollbars } from "react-custom-scrollbars-2";
import { HStack } from "@design-system/HStack/HStack";
import {
    StandardModalBody,
    StandardModalHeader,
} from "@design-system/Modal/Modal";
import { EditableTable } from "../../accounting/editableTable/EditableTableContext";
import styles from "./AddTransactionsModal.module.scss";
import {
    AddTransactionsModalFormikConfig,
    TransactionProxyObject,
} from "./AddTransactionsModal";
import { Footer } from "./Table/Footer";
import { useUpdateAndReturnNewLineCallback } from "./hooks";
import { AddTransactionsSpreadsheet } from "./AddTransactionsSpreadsheet";
import { AddTransactionsFinancialAccountSelect } from "./AddTransactionsFinancialAccountSelect";
import {
    AddTransactionTypePicker,
    TRANSACTION_TYPE,
    useCanAddCashItems,
    useOpenAddTransactionType,
} from "@components/checks/AddTransactionTypePicker";

interface Props {
    loading: boolean;
}

const hasEnteredValues = ({
    date,
    amount,
    description,
    category,
    accountId,
    classIds,
}: TransactionProxyObject): boolean =>
    !!date ||
    amount != null ||
    description.trim() !== "" ||
    category !== "" ||
    !!accountId ||
    !!classIds?.length;

export const AddTransactionsModalContent: React.FC<Props> = ({ loading }) => {
    const scrollbarsRef = useRef<Scrollbars>(null);

    const formikContext = useFormikContext<AddTransactionsModalFormikConfig>();
    const { values, setFieldValue } = formikContext;

    const transactions = values.transactions;

    const linesRef = React.useRef(transactions);

    React.useEffect(() => {
        linesRef.current = transactions;
    }, [transactions]);

    const updateAndReturnNewLine = useUpdateAndReturnNewLineCallback();
    const canAddCashItems = useCanAddCashItems();
    const openAddTransactionType = useOpenAddTransactionType();

    const addLines = useCallback(() => {
        const currentLines = linesRef.current;
        const newLines = [
            ...currentLines,
            ...new Array(5).fill(null).map(() => ({
                id: Math.random().toString(),
                accountCode: "",
                debitAmount: null,
                creditAmount: null,
                description: "",
            })),
        ];
        setFieldValue(`transactions`, newLines);
    }, [linesRef, setFieldValue]);

    return (
        <EditableTable<TransactionProxyObject>
            updateAndReturnNewLine={updateAndReturnNewLine}
            targetLines={transactions}
        >
            <>
                <StandardModalHeader>Add transaction</StandardModalHeader>
                <StandardModalBody className={styles.modalBody}>
                    <HStack gap={8} className={styles.modalBodyHeader}>
                        {canAddCashItems && (
                            <AddTransactionTypePicker
                                value={TRANSACTION_TYPE}
                                unsavedItemLabel={
                                    transactions.some(hasEnteredValues)
                                        ? "transaction"
                                        : undefined
                                }
                                onValueChange={(type) =>
                                    openAddTransactionType(
                                        type,
                                        values.financialAccountId,
                                    )
                                }
                                buttonProps={{ "aria-label": "Type" }}
                            />
                        )}
                        <AddTransactionsFinancialAccountSelect
                            value={values.financialAccountId}
                            onChange={({ financialAccountId, entityId }) => {
                                setFieldValue(
                                    "financialAccountId",
                                    financialAccountId,
                                );
                                setFieldValue("entityId", entityId);
                            }}
                        />
                    </HStack>

                    <Scrollbars
                        className={styles.spreadsheetContainer}
                        style={{ height: "435px" }}
                        ref={scrollbarsRef}
                    >
                        <AddTransactionsSpreadsheet />
                    </Scrollbars>
                </StandardModalBody>

                <Footer loading={loading} addLines={addLines} />
            </>
        </EditableTable>
    );
};
